"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { openChat } from "../../lib/chat-bus";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "News", href: "/blog" },
  { label: "Trainers", href: "/trainers" },
  { label: "Contact", href: "/contact" },
];

/**
 * NavGlassButtons — primary nav rendered as Apple-style liquid-glass
 * pill buttons. Pure CSS visuals (backdrop-filter + border + inner
 * highlight), works in every browser with backdrop-filter support.
 *
 * Two layouts share the same data set, switched purely via CSS:
 *   - Desktop ( > 540px): pills sit inline in a centred row.
 *   - Phone (≤ 540px):   a single hamburger button toggles a
 *                         vertical dropdown of the same pills.
 *
 * Rendering both at all viewport sizes (and hiding one with display:
 * none) keeps SSR markup stable — no hydration flash, no JS-driven
 * layout swap.
 */
export default function NavGlassButtons() {
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // close on Escape + on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [menuOpen]);

  return (
    <>
      {/* Desktop nav — visible only on viewports > 540px */}
      <nav className="jess-glass-nav jess-glass-nav--desktop" aria-label="Primary">
        {NAV_LINKS.map((l) => (
          <Link key={l.label} href={l.href} className="glass-pill">
            {l.label}
          </Link>
        ))}
        <button
          type="button"
          className="glass-pill glass-pill-cta"
          onClick={() => openChat("nav-glass")}
        >
          Ask Jess
        </button>
      </nav>

      {/* Mobile nav — hamburger toggle + dropdown, visible only ≤ 540px */}
      <nav
        ref={dropdownRef}
        className={`jess-glass-nav-mobile${menuOpen ? " is-open" : ""}`}
        aria-label="Primary mobile"
      >
        <button
          type="button"
          className="nav-menu-toggle"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-controls="jess-nav-mobile-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          {/* SVG icon — draws exactly the shape specified.  No flex
              layout, no parent-cascade, no iOS Safari quirks.
              Two paths: hamburger (3 lines) shown when closed,
              X (close glyph) shown when open. */}
          <svg
            width="22"
            height="16"
            viewBox="0 0 22 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{ display: "block" }}
          >
            {menuOpen ? (
              <>
                <line x1="3" y1="3" x2="19" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="19" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </>
            ) : (
              <>
                <line x1="1" y1="2" x2="21" y2="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="1" y1="8" x2="21" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="1" y1="14" x2="21" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </>
            )}
          </svg>
        </button>

        <div
          id="jess-nav-mobile-menu"
          className="nav-menu-dropdown"
          role="menu"
          aria-hidden={!menuOpen}
        >
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              href={l.href}
              className="glass-pill"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
            >
              {l.label}
            </Link>
          ))}
          <button
            type="button"
            className="glass-pill glass-pill-cta"
            role="menuitem"
            onClick={() => {
              openChat("nav-glass");
              setMenuOpen(false);
            }}
          >
            Ask Jess
          </button>
        </div>
      </nav>
    </>
  );
}
